// src/components/admin/mail/MailAccountTabs.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { MailAccount } from '../../../types/mail';
import { accentFor } from './mailUtils';

const Tabs = styled.div`
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
`;

const Tab = styled(motion.button)<{ $active: boolean; $gradient: string; $soft: string }>`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.8rem 1.4rem;
    border: 2px solid ${(p) => (p.$active ? 'transparent' : '#e2e8f0')};
    border-radius: 12px;
    background: ${(p) => (p.$active ? p.$gradient : '#f1f5f9')};
    color: ${(p) => (p.$active ? '#ffffff' : '#64748b')};
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    transition: all 0.3s ease;
    box-sizing: border-box;
    max-width: 100%;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 6px 20px ${(p) => p.$soft};
    }

    &:focus-visible {
        outline: 3px solid ${(p) => p.$soft};
        outline-offset: 2px;
    }
`;

const Labels = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const Name = styled.span`
    font-size: 0.98rem;
    line-height: 1.3;
`;

/** The full address is always on screen — never abbreviated to just "Práca". */
const Address = styled.span<{ $active: boolean }>`
    font-size: 0.76rem;
    font-weight: 500;
    color: ${(p) => (p.$active ? 'rgba(255,255,255,0.85)' : '#94a3b8')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const UnreadPill = styled.span<{ $active: boolean; $solid: string }>`
    background: ${(p) => (p.$active ? 'rgba(255,255,255,0.28)' : p.$solid)};
    color: #ffffff;
    border-radius: 20px;
    padding: 0.15rem 0.6rem;
    font-size: 0.76rem;
    font-weight: 700;
    min-width: 22px;
    text-align: center;
    flex-shrink: 0;
`;

const Warning = styled.span`
    display: inline-flex;
    color: #f59e0b;
    flex-shrink: 0;
`;

interface Props {
    accounts: MailAccount[];
    activeId: string | null;
    onChange: (id: string) => void;
}

/**
 * One pill per account.
 *
 * A unified inbox with per-account colour coding was considered and rejected: it makes the
 * reply identity depend on whichever row happens to be highlighted. Because messages are
 * only ever fetched per account, this arrangement makes the sending identity structural.
 */
const MailAccountTabs: React.FC<Props> = ({ accounts, activeId, onChange }) => (
    <Tabs role="tablist" aria-label="Poštové schránky">
        {accounts.map((account) => {
            const accent = accentFor(account.id);
            const active = account.id === activeId;
            return (
                <Tab
                    key={account.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    $active={active}
                    $gradient={accent.gradient}
                    $soft={accent.soft}
                    onClick={() => onChange(account.id)}
                    whileTap={{ scale: 0.98 }}
                >
                    <Labels>
                        <Name>{account.label}</Name>
                        <Address $active={active}>{account.address}</Address>
                    </Labels>
                    {!account.healthy && (
                        <Warning title={account.healthMessage || 'Schránka je nedostupná'}>
                            <AlertCircle size={16} />
                        </Warning>
                    )}
                    {account.healthy && account.unreadCount > 0 && (
                        <UnreadPill $active={active} $solid={accent.solid}>
                            {account.unreadCount}
                        </UnreadPill>
                    )}
                </Tab>
            );
        })}
    </Tabs>
);

export default MailAccountTabs;
